import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';

export interface UseFittedSlideWidthParams<T extends HTMLElement = HTMLElement> {
  /** The loaded deck, or null while it is still fetching/unavailable. */
  doc: PDFDocumentProxy | null;
  /**
   * 1-based page whose NATIVE aspect ratio bounds the fit, or null when there
   * is nothing to size. Read per-page so mixed portrait/letter decks each fit
   * correctly (MED-1274 AC6).
   */
  page: number | null;
  /**
   * The element the slide must fit inside. Its CONTENT box (padding already
   * excluded) is the available area — point this at the beige mat so the visual
   * grows to fill the mat and no further. Generic over the element type so a
   * concrete ref (e.g. `useRef<HTMLDivElement>`) assigns without a cast under
   * React 19's invariant `RefObject`.
   */
  containerRef: RefObject<T | null>;
  /** Render width used until the container + page have been measured. */
  fallbackWidth?: number;
  /**
   * Trailing debounce (ms) for resize-driven re-fits. Each width change
   * re-renders the PDF in SlidePageCanvas (its effect depends on `width`), so
   * rate-limit the churn while the user drags the window.
   */
  debounceMs?: number;
}

/**
 * MED-1274 — compute the render width that makes a slide page as large as
 * possible while fitting its container on BOTH axes (width and height),
 * preserving the page's aspect ratio (no distortion, no crop).
 *
 * The participant active-session view dedicates the whole screen to the deck,
 * yet the visual was hard-capped at 560px, leaving the beige mat as a large
 * empty field. Instead of a fixed width, this hook measures the mat and the
 * page and returns `min(availWidth, availHeight × aspect)` — so a landscape
 * page is bounded by the available height (side mat remains, by design — AC4)
 * and a portrait/letter page renders narrower and taller (AC6). Re-fits on
 * container resize, debounced (AC7).
 *
 * Kept out of the shared `SectionSlides` component so the coach whiteboard —
 * which renders through the same component at a fixed width — is unaffected.
 */
export function useFittedSlideWidth<T extends HTMLElement = HTMLElement>({
  doc,
  page,
  containerRef,
  fallbackWidth = 720,
  debounceMs = 120,
}: UseFittedSlideWidthParams<T>): number {
  // Native page dimensions (CSS px at scale 1) → aspect ratio.
  const [pageSize, setPageSize] = useState<{ w: number; h: number } | null>(
    null
  );
  // Available content box of the container (padding excluded).
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);

  // Re-read the page dimensions whenever the shown page (or deck) changes, so a
  // deck that mixes page sizes fits each page to its own aspect ratio (AC6).
  useEffect(() => {
    if (!doc || page == null) {
      setPageSize(null);
      return;
    }
    let cancelled = false;
    void doc
      .getPage(page)
      .then((p) => {
        if (cancelled) return;
        const viewport = p.getViewport({ scale: 1 });
        setPageSize({ w: viewport.width, h: viewport.height });
      })
      .catch(() => {
        // Leave pageSize null → width-only fit via the fallback aspect below.
      });
    return () => {
      cancelled = true;
    };
  }, [doc, page]);

  // Observe the container's content box. The first measurement is synchronous
  // (in the layout effect, before paint) so the initial render is already
  // fitted; subsequent resize callbacks are debounced.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const rect = el.getBoundingClientRect();
    const cs = window.getComputedStyle(el);
    const padX =
      parseFloat(cs.paddingLeft || '0') + parseFloat(cs.paddingRight || '0');
    const padY =
      parseFloat(cs.paddingTop || '0') + parseFloat(cs.paddingBottom || '0');
    setBox({ w: rect.width - padX, h: rect.height - padY });

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const size = entry.contentBoxSize?.[0];
        const w = size ? size.inlineSize : entry.contentRect.width;
        const h = size ? size.blockSize : entry.contentRect.height;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setBox({ w, h }), debounceMs);
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(debounceRef.current);
    };
  }, [containerRef, debounceMs]);

  if (!box || box.w <= 0 || box.h <= 0) return fallbackWidth;
  // Default to 16:9 until the real page dimensions arrive; the width is still
  // bounded by both axes so it never overflows, and it self-corrects to the
  // page's true aspect ratio within a frame or two.
  const aspect = pageSize && pageSize.h > 0 ? pageSize.w / pageSize.h : 16 / 9;
  const fitted = Math.min(box.w, box.h * aspect);
  return Math.max(1, Math.floor(fitted));
}
